import React, { useDeferredValue, Suspense, lazy } from "react";
import DashboardShell from "@/components/shell/DashboardShell";
import { useApp } from "@/contexts/AppContext";

// Standard components for initial paint
import OverviewTab from "@/components/overview/OverviewTab";
import DetailDrawer from "@/components/overview/DetailDrawer";

// Lazy components for LCP & Bundle size optimization
const GeoMapTab = lazy(() => import("@/components/map/GeoMapTab"));
const AnalysisTab = lazy(() => import("@/components/analysis/AnalysisTab"));
const LiveFeedTab = lazy(() => import("@/components/feed/LiveFeedTab"));
const AIAnalystPanel = lazy(() => import("@/components/ai/AIAnalystPanel"));
const PostDetailDialog = lazy(() => import("@/components/feed/PostDetailDialog"));

const DashboardSkeleton = () => (
  <div className="p-4 space-y-4 animate-pulse" data-testid="dashboard-skeleton">
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
      <div className="xl:col-span-12 h-[260px] bg-black/[0.03] border border-hair rounded-sm" />
      <div className="xl:col-span-12 h-[260px] bg-black/[0.03] border border-hair rounded-sm" />
    </div>
  </div>
);

export default function Dashboard() {
  const { activeTab } = useApp();
  // Deferred value ensures tab switching (UI feedback) is prioritized over heavy content rendering
  const deferredTab = useDeferredValue(activeTab);

  return (
    <DashboardShell>
      <div className="min-h-screen will-change-layout" data-testid="dashboard-root">
        <Suspense fallback={<DashboardSkeleton />}>
          <div className={deferredTab === "overview" ? "block p-4" : "hidden"}>
            {deferredTab === "overview" && <OverviewTab />}
          </div>
          <div className={deferredTab === "map" ? "block p-4" : "hidden"}>
            {deferredTab === "map" && <GeoMapTab />}
          </div>
          <div className={deferredTab === "analysis" ? "block p-4" : "hidden"}>
            {deferredTab === "analysis" && <AnalysisTab />}
          </div>
          <div className={deferredTab === "feed" ? "block p-4" : "hidden"}>
            {deferredTab === "feed" && <LiveFeedTab />}
          </div>
        </Suspense>
      </div>
      <DetailDrawer />
      <Suspense fallback={null}>
        <PostDetailDialog />
        <AIAnalystPanel />
      </Suspense>
    </DashboardShell>
  );
}
